import type { StateCreator } from 'zustand'
import type { LocalViewpoint } from '@viewer/lib/viewpointsStore'
import { viewpointsStore } from '@viewer/lib/viewpointsStore'
import type { FloatingRect } from './selectionSlice'
import { whenRecoveryResolved } from '@core/session/recovery'

// Detachable panel modes: same shape as the right sidebar so the
// look/feel stays consistent across detachable surfaces.
export type ViewpointsPanelMode = 'fixed' | 'floating' | 'window'

export interface ViewpointsSlice {
  viewpoints: LocalViewpoint[]
  viewpointsLoading: boolean
  viewpointsLoaded: boolean
  viewpointsPanelOpen: boolean
  viewpointsPanelMode: ViewpointsPanelMode
  viewpointsPanelWidth: number
  viewpointsPanelCollapsed: boolean
  viewpointsPanelFloating: FloatingRect

  refreshViewpoints: (opts?: { primaryHash?: string }) => Promise<void>
  upsertViewpointInList: (vp: LocalViewpoint) => void
  removeViewpointFromList: (id: string) => void
  setViewpointsPanelOpen: (open: boolean) => void
  setViewpointsPanelMode: (mode: ViewpointsPanelMode) => void
  setViewpointsPanelWidth: (w: number) => void
  setViewpointsPanelCollapsed: (collapsed: boolean) => void
  setViewpointsPanelFloating: (r: FloatingRect) => void
}

export const VIEWPOINTS_PANEL_MIN = 280
export const VIEWPOINTS_PANEL_MAX = 720
export const VIEWPOINTS_PANEL_DEFAULT = 360

const PANEL_OPEN_KEY = 'vn2r.viewpointsPanelOpen'
const PANEL_MODE_KEY = 'vn2r.viewpointsPanelMode'
const PANEL_WIDTH_KEY = 'vn2r.viewpointsPanelWidth'
const PANEL_COLLAPSED_KEY = 'vn2r.viewpointsPanelCollapsed'
const PANEL_FLOAT_KEY = 'vn2r.viewpointsPanelFloating'

function loadOpen(): boolean {
  try {
    return localStorage.getItem(PANEL_OPEN_KEY) === 'true'
  } catch {
    return false
  }
}

function loadMode(): ViewpointsPanelMode {
  try {
    const v = localStorage.getItem(PANEL_MODE_KEY)
    if (v === 'floating' || v === 'window' || v === 'fixed') return v
  } catch {
    /* ignore */
  }
  return 'fixed'
}

function loadWidth(): number {
  try {
    const v = localStorage.getItem(PANEL_WIDTH_KEY)
    if (v === null) return VIEWPOINTS_PANEL_DEFAULT
    const n = Number(v)
    if (!Number.isFinite(n)) return VIEWPOINTS_PANEL_DEFAULT
    return Math.min(VIEWPOINTS_PANEL_MAX, Math.max(VIEWPOINTS_PANEL_MIN, n))
  } catch {
    return VIEWPOINTS_PANEL_DEFAULT
  }
}

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(PANEL_COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

function defaultFloating(): FloatingRect {
  const w = 480
  const h = 600
  const x =
    typeof window !== 'undefined'
      ? Math.max(40, window.innerWidth - w - 480)
      : 40
  const y = 100
  return { x, y, w, h }
}

function loadFloating(): FloatingRect {
  try {
    const raw = localStorage.getItem(PANEL_FLOAT_KEY)
    if (!raw) return defaultFloating()
    const parsed = JSON.parse(raw) as Partial<FloatingRect>
    const def = defaultFloating()
    return {
      x: typeof parsed.x === 'number' ? parsed.x : def.x,
      y: typeof parsed.y === 'number' ? parsed.y : def.y,
      w: typeof parsed.w === 'number' ? parsed.w : def.w,
      h: typeof parsed.h === 'number' ? parsed.h : def.h,
    }
  } catch {
    return defaultFloating()
  }
}

function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

export const createViewpointsSlice: StateCreator<ViewpointsSlice> = (
  set,
  get,
) => ({
  viewpoints: [],
  viewpointsLoading: false,
  viewpointsLoaded: false,
  viewpointsPanelOpen: loadOpen(),
  viewpointsPanelMode: loadMode(),
  viewpointsPanelWidth: loadWidth(),
  viewpointsPanelCollapsed: loadCollapsed(),
  viewpointsPanelFloating: loadFloating(),

  async refreshViewpoints(opts) {
    if (get().viewpointsLoading) return
    set({ viewpointsLoading: true })
    // Mesmo portão do BCF: os viewpoints de uma sessão anterior só
    // entram depois da resposta ao diálogo de recuperação.
    await whenRecoveryResolved()
    try {
      const list = await viewpointsStore.list(opts)
      set({
        viewpoints: list,
        viewpointsLoaded: true,
        viewpointsLoading: false,
      })
    } catch (err) {
      console.warn('[viewpointsSlice] refresh failed', err)
      set({ viewpointsLoading: false })
    }
  },
  upsertViewpointInList(vp) {
    set((state) => {
      const next = [vp, ...state.viewpoints.filter((v) => v.id !== vp.id)]
      next.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      return { viewpoints: next }
    })
  },
  removeViewpointFromList(id) {
    set((state) => ({
      viewpoints: state.viewpoints.filter((v) => v.id !== id),
    }))
  },
  setViewpointsPanelOpen(open) {
    persist(PANEL_OPEN_KEY, String(open))
    set({ viewpointsPanelOpen: open })
  },
  setViewpointsPanelMode(mode) {
    persist(PANEL_MODE_KEY, mode)
    set({ viewpointsPanelMode: mode })
  },
  setViewpointsPanelWidth(w) {
    const clamped = Math.min(
      VIEWPOINTS_PANEL_MAX,
      Math.max(VIEWPOINTS_PANEL_MIN, w),
    )
    persist(PANEL_WIDTH_KEY, String(clamped))
    set({ viewpointsPanelWidth: clamped })
  },
  setViewpointsPanelCollapsed(collapsed) {
    persist(PANEL_COLLAPSED_KEY, String(collapsed))
    set({ viewpointsPanelCollapsed: collapsed })
  },
  setViewpointsPanelFloating(r) {
    persist(PANEL_FLOAT_KEY, JSON.stringify(r))
    set({ viewpointsPanelFloating: r })
  },
})
